/**
 * Untrusted-content boundary (untrusted-content-boundary design).
 *
 * Structurally tags tool-returned content that came from an attacker-controllable source (web/search,
 * subagent output, memory/graph recall, third-party tools) so prompt-injection payloads embedded in it
 * are framed as DATA, never instructions — by construction, not by hoping the model follows a rule. The
 * agent's own first-party working files (read/grep/find/ls/edit/write) are trusted and not wrapped.
 */

import { randomBytes } from "node:crypto";
import { UNTRUSTED_BOUNDARY_SYSTEM_RULE, UNTRUSTED_BOUNDARY_TAG } from "../provider-prompt-contracts.ts";

export { UNTRUSTED_BOUNDARY_SYSTEM_RULE };

export type ToolTrustLevel = "trusted" | "untrusted";

/** Tools whose output is attacker-controllable by default (name heuristic over built-ins + extensions). */
const UNTRUSTED_NAME_RE =
	/(fetch|search|web|browser|crawl|http|url|download|scrape|curl|wget|request|api|exec|execute|run[_-]?script|shell|subagent|delegate|recall|graph|automata)/i;
/** First-party tools that operate on the agent's own working scope — always trusted. */
const TRUSTED_BUILTINS = new Set(["read", "grep", "find", "ls", "edit", "write", "memory"]);

/**
 * Classify a tool's output trust. Precedence: explicit declared trust → trusted built-in → untrusted
 * name heuristic → trusted default. Local execution tools (`bash` and `python`) are trusted by default;
 * a deployment can opt them into wrapping by passing `bashUntrusted`.
 */
export function classifyToolTrust(
	toolName: string,
	opts?: { declaredTrust?: ToolTrustLevel; bashUntrusted?: boolean },
): ToolTrustLevel {
	if (opts?.declaredTrust) return opts.declaredTrust;
	const name = toolName.toLowerCase();
	if (name === "bash" || name === "python" || name === "powershell") {
		return opts?.bashUntrusted ? "untrusted" : "trusted";
	}
	if (TRUSTED_BUILTINS.has(name)) return "trusted";
	if (UNTRUSTED_NAME_RE.test(name)) return "untrusted";
	return "trusted";
}

/**
 * Wrap a single block of untrusted text in a nonce-fenced boundary. Neutralizes any attempt to break
 * out of (or spoof) the fence: literal boundary tags in the content are escaped, and any occurrence of
 * the random nonce is replaced so the model can always trust the real fence. Deterministic given the
 * nonce, so the prefix cache stays stable for a fixed result.
 */
export function wrapUntrustedText(
	text: string,
	source: string,
	options?: { nonce?: string; freshness?: string },
): string {
	const nonce = options?.nonce ?? randomBytes(16).toString("hex");
	// Neutralize fence spoofing tolerant to CASE and WHITESPACE variations (e.g. `</UNTRUSTED_CONTENT>`
	// or `< / untrusted_content >`), which LLMs still read as a closing tag. Match an angle bracket,
	// optional slash/whitespace, then the tag name in any case.
	const neutralized = text
		.replace(/<(\s*\/?\s*)untrusted_content/gi, "&lt;$1untrusted_content")
		.replaceAll(nonce, "[NONCE_NEUTRALIZED]");
	const freshnessAttr = options?.freshness ? ` freshness="${escapeAttr(options.freshness)}"` : "";
	return `<${UNTRUSTED_BOUNDARY_TAG} id="${nonce}" source="${escapeAttr(source)}"${freshnessAttr}>\n${neutralized}\n</${UNTRUSTED_BOUNDARY_TAG}>`;
}

function escapeAttr(value: string): string {
	return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
